import { NOTICE_TEXT_MAX, type NoticeKind, type PayloadOf } from '@gk/contracts'
import type { AreaRow } from './geo.ts'
import { UNIT_TYPES, areasSummary, unitShort } from './planUi.ts'
import { useMerged } from '../sync/hooks.ts'

/** The course notices on the device (M16-B1, docs/M16.md "B1"): the rules of the screens, pure, with their tests. A notice is a short note whoever runs
 *  the course writes for the team; the device reads its mirrored copy (`ops.course_notice`, both profiles) and shows the notices IN FORCE on a day
 *  wherever that day is shown: Today's first card (M16-A), the shed screen, the day board's header (placed by Z), the week grid's header (placed by F),
 *  and the printable "État du parcours" for the reception desk. Nothing here decides anything: a notice is text, areas and dates. */
export interface NoticeRow {
  id: string; kind: NoticeKind; area_ids: string[]; text: { fr?: string; en?: string }
  starts_on: string; ends_on: string; start_time: string | null; status: 'live' | 'retired'
  approved_for_public: boolean; approved_at: string | null; revision: number
}
export const useNotices = () => useMerged<NoticeRow>('course_notice', 'ops.course_notice')

export type NoticeLike = Pick<NoticeRow, 'id' | 'kind' | 'starts_on' | 'ends_on' | 'start_time' | 'status'>
export type NoticeShown = NoticeLike & Pick<NoticeRow, 'area_ids' | 'text'>
type T = (k: string, o?: Record<string, unknown>) => string

/** The i18n key of each kind (a static map, so every key is a literal the catalogue test can hold). */
export const NOTICE_KIND_KEY: Record<NoticeKind, string> = {
  closure: 'courseStatus.kind.closure', restriction: 'courseStatus.kind.restriction', maintenance: 'courseStatus.kind.maintenance',
  info: 'courseStatus.kind.info', pins: 'courseStatus.kind.pins', event: 'courseStatus.kind.event',
}
/** The order of the lines of one day: what the morning asks first (a competition and its start), then what is closed, restricted, worked on, the pins, the rest. */
const KIND_RANK: Record<NoticeKind, number> = { event: 0, closure: 1, restriction: 2, maintenance: 3, pins: 4, info: 5 }

const byRank = <N extends NoticeLike>(a: N, b: N): number =>
  (KIND_RANK[a.kind] ?? 9) - (KIND_RANK[b.kind] ?? 9) || (a.start_time ?? '').localeCompare(b.start_time ?? '') || a.starts_on.localeCompare(b.starts_on) || a.id.localeCompare(b.id)

/** The live notices in force on `date` (a local day of the site), in the order the lines are shown. A retired notice is on no list. */
export function noticesFor<N extends NoticeLike>(notices: readonly N[] | undefined, date: string): N[] {
  return (notices ?? []).filter((n) => n.status === 'live' && n.starts_on <= date && date <= n.ends_on).sort(byRank)
}

/** The management page's three groups of LIVE notices: in force today, to come (soonest first), over (latest first). */
export function groupNotices<N extends NoticeLike>(notices: readonly N[] | undefined, today: string): { current: N[]; upcoming: N[]; past: N[] } {
  const live = (notices ?? []).filter((n) => n.status === 'live')
  return {
    current: noticesFor(live, today),
    upcoming: live.filter((n) => n.starts_on > today).sort((a, b) => a.starts_on.localeCompare(b.starts_on) || byRank(a, b)),
    past: live.filter((n) => n.ends_on < today).sort((a, b) => b.ends_on.localeCompare(a.ends_on) || byRank(a, b)),
  }
}

/** The text in the person's language, else the other one (a notice may be written in one language only). */
export function noticeText(text: NoticeRow['text'] | null | undefined, lang: string): string {
  const l = lang.startsWith('en') ? 'en' : 'fr'
  return (text?.[l] ?? text?.[l === 'en' ? 'fr' : 'en'] ?? '').trim()
}

/** "08:00" → "8 h" (fr) or "8:00" (en); "08:30" → "8 h 30" / "8:30". The club's way of writing a start time on a sheet. */
export function startTimeText(time: string | null | undefined, lang: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(time ?? '')
  if (!m) return ''
  const h = String(Number(m[1])), mm = m[2]!
  if (lang.startsWith('en')) return `${h}:${mm}`
  return mm === '00' ? `${h} h` : `${h} h ${mm}`
}

/** The areas of a notice as one text: a unit of play by its short word ("Rouge": M23's rule for the loops), the other areas as the board summarises
 *  them ("Greens 1–9 · Rouge"), the whole course when none is named. */
export function noticeAreasText(ids: readonly string[], areaById: Map<string, AreaRow>, lang: string, t: T): string {
  if (ids.length === 0) return t('courseStatus.wholeSite')
  const units = ids.flatMap((id) => { const a = areaById.get(id); return a && UNIT_TYPES.includes(a.area_type_code) ? [unitShort(a, lang, t)] : [] })
  const rest = ids.filter((id) => { const a = areaById.get(id); return !(a && UNIT_TYPES.includes(a.area_type_code)) })
  return [...units, ...(rest.length > 0 ? [areasSummary(rest, areaById, lang, t)] : [])].join(' ; ')
}

/** The one line of a notice: "Compétition · Rouge · Coupe du club · départ 8 h" (the kind, the areas, the text, the start). */
export function noticeLineText(n: NoticeShown, areaById: Map<string, AreaRow>, lang: string, t: T): string {
  const start = n.start_time ? t('courseStatus.startAt', { time: startTimeText(n.start_time, lang) }) : ''
  return [t(NOTICE_KIND_KEY[n.kind]), noticeAreasText(n.area_ids, areaById, lang, t), noticeText(n.text, lang), start].filter(Boolean).join(' · ')
}

const dtf = (lang: string, opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(lang, { ...opts, timeZone: 'UTC' })
/** "1 mai": a calendar day has no time zone, so it is formatted as UTC noon (the board's rule). */
export const dayText = (iso: string, lang: string): string => dtf(lang, { day: 'numeric', month: 'long' }).format(new Date(`${iso}T12:00:00Z`))
/** "du 1 mai au 3 mai", or "le 1 mai" for a one-day notice. */
export function noticeDatesText(n: Pick<NoticeLike, 'starts_on' | 'ends_on'>, lang: string, t: T): string {
  return n.starts_on === n.ends_on ? t('courseStatus.oneDay', { day: dayText(n.starts_on, lang) }) : t('courseStatus.dates', { from: dayText(n.starts_on, lang), to: dayText(n.ends_on, lang) })
}

// ---------------------------------------------------------------------------------------------------------------------
// The form (Réglages › État du parcours): what the person types, what is missing, what is sent
// ---------------------------------------------------------------------------------------------------------------------
export interface NoticeForm { kind: NoticeKind; fr: string; en: string; areaIds: string[]; startsOn: string; endsOn: string; startTime: string }
export const NOTICE_PROBLEMS = ['text', 'textLong', 'dates', 'time'] as const
export type NoticeProblem = (typeof NOTICE_PROBLEMS)[number]
/** The line beside the disabled primary, per problem (a static map: literal keys for the catalogue test). */
export const NOTICE_PROBLEM_KEY: Record<NoticeProblem, string> = {
  text: 'courseStatus.problem.text', textLong: 'courseStatus.problem.textLong', dates: 'courseStatus.problem.dates', time: 'courseStatus.problem.time',
}
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/

export const emptyNoticeForm = (today: string): NoticeForm => ({ kind: 'info', fr: '', en: '', areaIds: [], startsOn: today, endsOn: today, startTime: '' })
export const formOf = (n: NoticeRow): NoticeForm => ({ kind: n.kind, fr: n.text.fr ?? '', en: n.text.en ?? '', areaIds: [...n.area_ids], startsOn: n.starts_on, endsOn: n.ends_on, startTime: n.start_time ? n.start_time.slice(0, 5) : '' })

/** What keeps the primary disabled, in the order the hint says it (M23 item 6): the first problem is the line shown. */
export function noticeProblems(f: NoticeForm): NoticeProblem[] {
  const out: NoticeProblem[] = []
  const fr = f.fr.trim(), en = f.en.trim()
  if (!fr && !en) out.push('text')
  if ([...fr].length > NOTICE_TEXT_MAX || [...en].length > NOTICE_TEXT_MAX) out.push('textLong')
  if (!ISO_DAY.test(f.startsOn) || !ISO_DAY.test(f.endsOn) || f.endsOn < f.startsOn) out.push('dates')
  if (f.startTime !== '' && !TIME.test(f.startTime)) out.push('time')
  return out
}

const textOf = (f: NoticeForm): { fr?: string; en?: string } => ({ ...(f.fr.trim() ? { fr: f.fr.trim() } : {}), ...(f.en.trim() ? { en: f.en.trim() } : {}) })
const sameIds = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i])

/** The payload of course_notice.write@1 (a trimmed text, an empty language dropped, an empty time null). */
export function writePayload(f: NoticeForm, id: string): PayloadOf<'course_notice.write@1'> {
  return { notice_id: id, kind: f.kind, text: textOf(f), area_ids: [...f.areaIds], starts_on: f.startsOn, ends_on: f.endsOn, start_time: f.startTime || null }
}
/** The `set` of course_notice.update@1: only what differs from the row the editor read; null when nothing changed. */
export function updateSet(f: NoticeForm, n: NoticeRow): PayloadOf<'course_notice.update@1'>['set'] | null {
  const set: PayloadOf<'course_notice.update@1'>['set'] = {}
  if (f.kind !== n.kind) set.kind = f.kind
  const text = textOf(f)
  if ((text.fr ?? '') !== (n.text.fr ?? '') || (text.en ?? '') !== (n.text.en ?? '')) set.text = text
  if (!sameIds(f.areaIds, n.area_ids)) set.area_ids = [...f.areaIds]
  if (f.startsOn !== n.starts_on) set.starts_on = f.startsOn
  if (f.endsOn !== n.ends_on) set.ends_on = f.endsOn
  if ((f.startTime || null) !== (n.start_time ? n.start_time.slice(0, 5) : null)) set.start_time = f.startTime || null
  return Object.keys(set).length > 0 ? set : null
}
